import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import InvoicePrintModal from '../components/InvoicePrintModal';

const SaleEntry = () => {
  const { selectedFirmId, firms, company } = useContext(AuthContext);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);

  // Form State
  const [saleId, setSaleId] = useState(null);
  const [saleNo, setSaleNo] = useState('1');
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [customerId, setCustomerId] = useState('');

  // Line items [{ productId, kgperbox, noofbox, addqty, Qty, disc_qty, net_qty, Rate, total }]
  const [items, setItems] = useState([
    { productId: '', kgperbox: 0, noofbox: 0, addqty: 0, Qty: 0, disc_qty: 0, net_qty: 0, Rate: 0, total: 0 }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Print & WhatsApp Modal State
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [waSettings, setWaSettings] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMasters();
    loadSales();
  }, [selectedFirmId]);

  const loadMasters = async () => {
    try {
      const [custRes, prodRes, waRes] = await Promise.all([
        api.get('/masters/customers'),
        api.get(`/masters/products?firmId=${selectedFirmId}`),
        api.get('/whatsapp/settings').catch(() => null)
      ]);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
      if (waRes?.data?.settings) {
        setWaSettings(waRes.data.settings);
      }
    } catch (err) {
      console.error('Error loading sales masters', err);
    }
  };

  const loadSales = async () => {
    try {
      const res = await api.get(`/sales?firmId=${selectedFirmId}`);
      setSales(res.data);
      if (res.data.length > 0 && !saleId) {
        const maxNo = Math.max(...res.data.map(s => Number(s.saleNo) || 0));
        setSaleNo(String(maxNo + 1));
      }
    } catch (err) {
      console.error('Error loading sales', err);
    }
  };

  // Auto Calculations
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === 'productId') {
      const prod = products.find(p => String(p.Id) === String(value));
      if (prod) {
        updated[index].Rate = prod.SRate || 0;
      }
    }

    const kgPerBox = parseFloat(updated[index].kgperbox) || 0;
    const noOfBox = parseFloat(updated[index].noofbox) || 0;
    const addQty = parseFloat(updated[index].addqty) || 0;
    const discQty = parseFloat(updated[index].disc_qty) || 0;
    const rate = parseFloat(updated[index].Rate) || 0;

    // Calculate total quantity
    const totalQty = (kgPerBox * noOfBox) + addQty;
    const netQty = Math.max(0, totalQty - discQty);
    const itemTotal = netQty * rate;

    updated[index].Qty = totalQty;
    updated[index].net_qty = netQty;
    updated[index].total = itemTotal;

    setItems(updated);
  };

  const addItemRow = () => {
    setItems([
      ...items,
      { productId: '', kgperbox: 0, noofbox: 0, addqty: 0, Qty: 0, disc_qty: 0, net_qty: 0, Rate: 0, total: 0 }
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const grandTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

  const handleSaveSale = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!customerId) {
      setError('Please select a customer for the sale invoice.');
      return;
    }

    const validItems = items.filter(i => i.productId && i.total > 0);
    if (validItems.length === 0) {
      setError('Please add at least one valid product line item.');
      return;
    }

    const payload = {
      Id: saleId,
      saleNo: parseInt(saleNo) || 1,
      saleDate,
      customerId,
      GrandTotal: grandTotal,
      FirmId: selectedFirmId,
      FID: 4,
      items: validItems
    };

    setLoading(true);
    try {
      const saveRes = await api.post('/sales', payload);
      const savedId = saveRes.data.id || saleId;
      setSuccess('Sales invoice saved successfully!');
      resetForm();
      loadSales();

      // Check if auto popup print/whatsapp setting is enabled (defaults to true)
      const autoPopupEnabled = waSettings ? Boolean(waSettings.AutoSendSale === 1 || waSettings.AutoSendSale === true) : true;

      if (savedId && autoPopupEnabled) {
        setTimeout(() => {
          handleOpenPrintModal(savedId);
        }, 300);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save sales invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPrintModal = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`/sales/${id}`);
      const { sale: sData, details } = res.data;
      const currentFirm = firms.find(f => String(f.Id) === String(sData.FirmId || selectedFirmId));

      setPrintData({
        saleNo: sData.saleNo,
        saleDate: sData.saleDate,
        customerName: sData.customerName,
        customerAddress: sData.customerAddress,
        customerMobile: sData.customerMobile,
        firmName: sData.firmName || currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
        firmAddress: sData.firmAddress || currentFirm?.Address || company?.Comapny_Address || '',
        firmContact: sData.firmContact || currentFirm?.ContactNo || company?.Company_Mobile || '',
        items: details,
        GrandTotal: sData.GrandTotal,
        balance: sData.balance || 0
      });
      setIsPrintOpen(true);
    } catch (err) {
      console.error('Error opening print modal', err);
      setError('Failed to load bill for printing');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewCurrentBill = () => {
    const currentCust = customers.find(c => String(c.Id) === String(customerId));
    const currentFirm = firms.find(f => String(f.Id) === String(selectedFirmId));

    const populatedItems = items
      .filter(i => i.productId)
      .map(i => {
        const p = products.find(prod => String(prod.Id) === String(i.productId));
        return {
          ...i,
          productName: p ? p.productName : 'Fish Item'
        };
      });

    if (populatedItems.length === 0) {
      alert('प्रिंट करण्यासाठी किमान एक मासा / आयटम जोडा (Please add at least one item to preview)');
      return;
    }

    setPrintData({
      saleNo: saleNo || '1',
      saleDate: saleDate,
      customerName: currentCust ? currentCust.Name : 'Customer',
      customerAddress: currentCust ? currentCust.Address : '',
      customerMobile: currentCust ? currentCust.MobileNo : '',
      firmName: currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
      firmAddress: currentFirm?.Address || company?.Comapny_Address || 'Sakhar Koliwada, Alibag, Raigad',
      firmContact: currentFirm?.ContactNo || company?.Company_Mobile || '9881914831',
      items: populatedItems,
      GrandTotal: grandTotal,
      balance: 0
    });
    setIsPrintOpen(true);
  };

  const handleEditSale = async (sale) => {
    try {
      setLoading(true);
      const res = await api.get(`/sales/${sale.Id}`);
      const { sale: sData, details } = res.data;

      setSaleId(sData.Id);
      setSaleNo(String(sData.saleNo));
      setSaleDate(sData.saleDate);
      setCustomerId(sData.customerId);

      setItems(details.length > 0 ? details : [
        { productId: '', kgperbox: 0, noofbox: 0, addqty: 0, Qty: 0, disc_qty: 0, net_qty: 0, Rate: 0, total: 0 }
      ]);
    } catch (err) {
      setError('Failed to load sale invoice details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSale = async (id) => {
    if (!window.confirm('Delete sales invoice?')) return;
    try {
      await api.delete(`/sales/${id}`);
      setSuccess('Sales invoice deleted');
      loadSales();
    } catch (err) {
      setError('Failed to delete sale');
    }
  };

  const resetForm = () => {
    setSaleId(null);
    setCustomerId('');
    setItems([{ productId: '', kgperbox: 0, noofbox: 0, addqty: 0, Qty: 0, disc_qty: 0, net_qty: 0, Rate: 0, total: 0 }]);
  };

  return (
    <div className="app-container container-fluid py-3 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 className="fw-extrabold mb-1 text-primary fs-4" style={{ fontFamily: 'var(--app-font-heading)' }}>
            <i className="bi bi-receipt me-2"></i>व्यापारी बील / Sales Invoice Entry
          </h3>
          <p className="text-muted mb-0 small">Wholesale fish sales bill entry with box & discount calculation</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {success && <div className="alert alert-success py-2 small">{success}</div>}

      <div className="row g-4">
        {/* FORM SECTION */}
        <div className="col-12 col-xl-8">
          <form onSubmit={handleSaveSale}>
            {/* INVOICE HEADER CARD */}
            <div className="card border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
              <div className="card-header bg-primary text-white fw-bold d-flex justify-content-between align-items-center py-3 px-4">
                <span className="d-flex align-items-center gap-2 fs-6">
                  <i className="bi bi-file-earmark-text-fill fs-5"></i>
                  बील तपशील / Invoice Header
                </span>
                {saleId && <span className="badge bg-warning text-dark px-3 py-1 fs-6">Editing Invoice #{saleNo}</span>}
              </div>
              <div className="card-body p-4 bg-white">
                <div className="row g-3">
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-hash text-primary me-1"></i>
                      बील क्रमांक (Bill No) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg fw-bold border-primary-subtle shadow-sm"
                      value={saleNo}
                      onChange={(e) => setSaleNo(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-calendar3 text-primary me-1"></i>
                      तारीख (Date) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-lg border-primary-subtle shadow-sm"
                      value={saleDate}
                      onChange={(e) => setSaleDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-person-fill text-primary me-1"></i>
                      व्यापारी नांव (Customer) <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg fw-semibold border-primary-subtle shadow-sm"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      required
                    >
                      <option value="">-- ग्राहक / व्यापारी निवडा --</option>
                      {customers.map(c => (
                        <option key={c.Id} value={c.Id}>
                          {c.Name} {c.Address ? `(${c.Address})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* PRODUCT ITEMS LIST CARD */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0 text-dark">
                  <i className="bi bi-list-check me-2 text-primary"></i>सामान यादी / Sale Items
                </h6>
                <button type="button" className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={addItemRow}>
                  <i className="bi bi-plus-lg me-1"></i> नवीन ओळ (+ Add Item)
                </button>
              </div>

              {/* MOBILE ITEM CARDS VIEW (< 768px) */}
              <div className="d-block d-md-none p-2 bg-light">
                {items.map((row, idx) => (
                  <div key={idx} className="card border mb-3 shadow-sm rounded-3">
                    <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                      <span className="fw-bold small text-primary">मासा # {idx + 1}</span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm py-0 px-2"
                          onClick={() => removeItemRow(idx)}
                        >
                          <i className="bi bi-trash"></i> काढून टाका
                        </button>
                      )}
                    </div>
                    <div className="card-body p-3">
                      <div className="mb-3">
                        <label className="form-label fw-semibold small">माशाचे नांव (Fish Product)</label>
                        <select
                          className="form-select"
                          value={row.productId}
                          onChange={(e) => handleItemChange(idx, 'productId', e.target.value)}
                        >
                          <option value="">-- मासा निवडा --</option>
                          {products.map(p => (
                            <option key={p.Id} value={p.Id}>{p.productName}</option>
                          ))}
                        </select>
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">Kg/Box</label>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.kgperbox}
                            onChange={(e) => handleItemChange(idx, 'kgperbox', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">पेटी Boxes</label>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.noofbox}
                            onChange={(e) => handleItemChange(idx, 'noofbox', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">इतर Qty</label>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.addqty}
                            onChange={(e) => handleItemChange(idx, 'addqty', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">सूट Disc Qty</label>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.disc_qty}
                            onChange={(e) => handleItemChange(idx, 'disc_qty', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">दर Rate (₹)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="form-control form-control-sm text-center fw-bold text-primary"
                            value={row.Rate}
                            onChange={(e) => handleItemChange(idx, 'Rate', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">निव्वळ Qty</label>
                          <div className="form-control form-control-sm bg-light text-center fw-bold">{row.net_qty}</div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                        <span className="small text-muted fw-bold">एकूण रक्कम (Item Total):</span>
                        <span className="fw-bold fs-5 text-success">₹{(row.total || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* DESKTOP RESPONSIVE TABLE VIEW (>= 768px) */}
              <div className="table-responsive d-none d-md-block" style={{ overflowX: 'auto' }}>
                <table className="table table-bordered align-middle mb-0 small" style={{ minWidth: '840px' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '180px' }}>माशाचे नांव (Fish Item)</th>
                      <th style={{ minWidth: '75px' }}>Kg/Box</th>
                      <th style={{ minWidth: '75px' }}>पेटी Boxes</th>
                      <th style={{ minWidth: '75px' }}>Add Qty</th>
                      <th style={{ minWidth: '75px' }}>Disc Qty</th>
                      <th style={{ minWidth: '75px' }}>Net Qty</th>
                      <th style={{ minWidth: '90px' }}>दर Rate</th>
                      <th style={{ minWidth: '110px' }}>एकूण (₹)</th>
                      <th style={{ minWidth: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={row.productId}
                            onChange={(e) => handleItemChange(idx, 'productId', e.target.value)}
                          >
                            <option value="">-- मासा निवडा --</option>
                            {products.map(p => (
                              <option key={p.Id} value={p.Id}>{p.productName}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.kgperbox}
                            onChange={(e) => handleItemChange(idx, 'kgperbox', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.noofbox}
                            onChange={(e) => handleItemChange(idx, 'noofbox', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.addqty}
                            onChange={(e) => handleItemChange(idx, 'addqty', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.disc_qty}
                            onChange={(e) => handleItemChange(idx, 'disc_qty', e.target.value)}
                          />
                        </td>
                        <td className="fw-bold bg-light text-center">{row.net_qty}</td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            className="form-control form-control-sm text-center fw-bold"
                            value={row.Rate}
                            onChange={(e) => handleItemChange(idx, 'Rate', e.target.value)}
                          />
                        </td>
                        <td className="fw-bold text-success">₹{(row.total || 0).toLocaleString('en-IN')}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm p-0 px-2"
                            onClick={() => removeItemRow(idx)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* CARD FOOTER WITH GRAND TOTAL */}
              <div className="card-footer bg-light d-flex justify-content-between align-items-center py-3">
                <span className="fw-bold fs-6">एकूण बील (Grand Total Amount):</span>
                <span className="fs-3 fw-extrabold text-success">₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="d-flex gap-2 mb-4 flex-wrap">
              <button type="submit" className="btn btn-primary btn-lg flex-grow-1 fw-bold shadow-sm" disabled={loading}>
                <i className="bi bi-check-circle me-2"></i> बील सेव्ह करा (Save Invoice)
              </button>
              <button
                type="button"
                className="btn btn-outline-dark btn-lg fw-bold shadow-sm"
                onClick={handlePreviewCurrentBill}
                disabled={items.filter(i => i.productId).length === 0}
                title="बील प्रिंट किंवा व्हॉट्सॲपवर पाहण्यासाठी"
              >
                <i className="bi bi-printer me-2"></i> प्रिंट / व्हॉट्सॲप (Print / Share)
              </button>
              {saleId && (
                <button type="button" className="btn btn-outline-secondary btn-lg" onClick={resetForm}>
                  रद्द करा (Cancel)
                </button>
              )}
            </div>
          </form>
        </div>

        {/* RECENT SALES HISTORY TABLE */}
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history me-2 text-primary"></i>मागील बील (Sales History)
              </h6>
              <span className="badge bg-primary-subtle text-primary fw-semibold">{sales.length} Bills</span>
            </div>
            <div className="table-responsive" style={{ maxHeight: '550px' }}>
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>बील #</th>
                    <th>व्यापारी (Customer)</th>
                    <th>एकूण (Total)</th>
                    <th className="text-end">कृती (Actions)</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map(s => (
                    <tr key={s.Id}>
                      <td className="fw-bold">#{s.saleNo}</td>
                      <td>
                        <div className="fw-semibold">{s.customerName || 'Customer'}</div>
                        {s.customerMobile && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            <i className="bi bi-phone me-1"></i>{s.customerMobile}
                          </div>
                        )}
                      </td>
                      <td className="text-success fw-bold">₹{(s.GrandTotal || 0).toLocaleString('en-IN')}</td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-dark py-0 px-2"
                            title="ऑनलाइन बील प्रिंट करा (Online Print)"
                            onClick={() => handleOpenPrintModal(s.Id)}
                          >
                            <i className="bi bi-printer"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success py-0 px-2"
                            title="व्हॉट्सॲपवर पाठवा (Send WhatsApp)"
                            onClick={() => handleOpenPrintModal(s.Id)}
                          >
                            <i className="bi bi-whatsapp"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary py-0 px-2"
                            title="बदला (Edit)"
                            onClick={() => handleEditSale(s)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger py-0 px-2"
                            title="हटवा (Delete)"
                            onClick={() => handleDeleteSale(s.Id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-3">
                        कोणतीही मागील बीले उपलब्ध नाहीत (No sales records)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* FISH SHOP CASH MEMO ONLINE PRINT & WHATSAPP MODAL */}
      <InvoicePrintModal
        isOpen={isPrintOpen}
        onClose={() => setIsPrintOpen(false)}
        title="कॅश मेमो / व्यापारी विक्री बील (Sales Bill)"
        invoiceType="sale"
        data={printData}
      />
    </div>
  );
};

export default SaleEntry;

